export const QUICK_OPEN_ENTITY_TYPES = {
  client: {
    key: "client",
    label: "Client",
    shortLabel: "Client",
    description: "Profile & records",
  },
  education: {
    key: "education",
    label: "Education application",
    shortLabel: "Education",
    description: "Study application",
  },
  migration: {
    key: "migration",
    label: "Migration application",
    shortLabel: "Migration",
    description: "Visa application",
  },
};

export const QUICK_OPEN_ENTITY_ORDER = [
  "client",
  "education",
  "migration",
];

const SESSION_ENTITY_KEY = "cms_quick_open_entity_type";

/** Record IDs are numeric only */
const RECORD_ID_PATTERN = /^\d+$/;

export function getStoredQuickOpenEntityType() {
  try {
    const value = sessionStorage.getItem(SESSION_ENTITY_KEY);
    if (value && QUICK_OPEN_ENTITY_TYPES[value]) return value;
  } catch {
    /* ignore */
  }
  return "client";
}

export function storeQuickOpenEntityType(entityKey) {
  if (!QUICK_OPEN_ENTITY_TYPES[entityKey]) return;
  try {
    sessionStorage.setItem(SESSION_ENTITY_KEY, entityKey);
  } catch {
    /* ignore */
  }
}

function normalizeRecordId(raw) {
  const id = String(raw ?? "").trim().replace(/\/$/, "");
  if (!id || !RECORD_ID_PATTERN.test(id)) return null;
  return id;
}

/** If user pastes a CMS URL or path, detect type and id. */
export function tryParseQuickOpenFromUrlOrPath(input) {
  const text = String(input ?? "").trim();
  if (!text) return null;

  const clientMatch = text.match(/view-client-detail\/([^/?#\s]+)/i);
  if (clientMatch) {
    const id = normalizeRecordId(clientMatch[1]);
    if (id) return { entityKey: "client", id };
  }

  const eduMatch = text.match(/view-education-application-detail\/([^/?#\s]+)/i);
  if (eduMatch) {
    const id = normalizeRecordId(eduMatch[1]);
    if (id) return { entityKey: "education", id };
  }

  const migMatch = text.match(/view-migration-application-detail\/([^/?#\s]+)/i);
  if (migMatch) {
    const id = normalizeRecordId(migMatch[1]);
    if (id) return { entityKey: "migration", id };
  }

  return null;
}

export function resolveQuickOpenTarget(entityKey, rawInput) {
  const fromLink = tryParseQuickOpenFromUrlOrPath(rawInput);
  if (fromLink) return fromLink;

  const id = normalizeRecordId(rawInput);
  if (!id || !QUICK_OPEN_ENTITY_TYPES[entityKey]) return null;

  return { entityKey, id };
}

export function buildQuickOpenPath(entityKey, rawId) {
  const id = normalizeRecordId(rawId);
  if (!id) return null;

  switch (entityKey) {
    case "client":
      return `/clients/view-client-detail/${encodeURIComponent(id)}/`;
    case "education":
      return `/applications/view-education-application-detail/${encodeURIComponent(id)}`;
    case "migration":
      return `/applications/view-migration-application-detail/${encodeURIComponent(id)}`;
    default:
      return null;
  }
}

export function buildQuickOpenAbsoluteUrl(entityKey, rawId) {
  const path = buildQuickOpenPath(entityKey, rawId);
  if (!path) return null;
  return `${window.location.origin}${path}`;
}

export function openQuickOpenRecord(entityKey, rawInput, { newTab = true } = {}) {
  const target = resolveQuickOpenTarget(entityKey, rawInput);
  if (!target) {
    return {
      ok: false,
      error: "Enter a valid numeric record ID or paste a record link from the browser.",
    };
  }

  const path = buildQuickOpenPath(target.entityKey, target.id);
  if (!path) {
    return {
      ok: false,
      error: "Enter a valid numeric record ID or paste a record link from the browser.",
    };
  }

  if (newTab) {
    const url = `${window.location.origin}${path}`;
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    window.location.assign(path);
  }

  return { ok: true, path, entityKey: target.entityKey, id: target.id };
}
